import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  try {
    if (cardCode.length < 3) {
      return null;
    }
    const cleanCode = cardCode.replace(/\.(jpg|png)$/i, '');
    return `/assets/img/cards/${cleanCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to parse card string and split into Player and Banker cards
const parseCards = (cardString) => {
  if (!cardString) return { player: [], banker: [] };
  
  const cards = cardString.split(',');
  const playerCards = cards.slice(0, 3).map(card => getCardImage(card.trim()));
  const bankerCards = cards.slice(3, 6).map(card => getCardImage(card.trim()));
  
  return { player: playerCards, banker: bankerCards };
};

// Helper function to parse rdesc: "Player#-#9" (Winner#WinnerPair#Score)
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#');
  return {
    winner: parts[0] || "",
    winnerPair: parts[1] || "-",
    score: parts[2] || ""
  };
};

const ResultBaccarat2 = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('baccarat2');
        if (response?.success) {
          if (response?.data?.data?.res && Array.isArray(response.data.data.res)) {
            setLastResults(response.data.data.res);
          } else if (response?.data?.res && Array.isArray(response.data.res)) {
            setLastResults(response.data.res);
          }
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);
    
    const mid = resultItem?.mid;
    
    if (mid) {
      try {
        const response = await getDetailResults('baccarat2', mid.toString());
        if (response?.success) {
          if (response?.data?.data?.t1) {
            setDetailResult(response.data.data.t1);
          } else if (response?.data?.t1) {
            setDetailResult(response.data.t1);
          }
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal || !detailResult) return null;

    const cards = parseCards(detailResult.card);
    const rdescData = parseRdesc(detailResult.rdesc);
    const winner = detailResult.winnat || "";
    const winValue = detailResult.win;
    const isPlayerWinner = winValue === "1" || winner === "Player";
    const isBankerWinner = winValue === "2" || winner === "Banker";
    const isTie = winValue === "3" || winner === "Tie";

    return {
      roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      player: {
        cards: cards.player,
        isWinner: isPlayerWinner,
      },
      banker: {
        cards: cards.banker,
        isWinner: isBankerWinner,
      },
      rdescData: rdescData,
      isTie: isTie,
    };
  }, [selectedResult, detailResult, showModal, gameData]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults
    : data.map((res, idx) => ({ win: res === "A" ? "1" : res === "B" ? "2" : "3", mid: null }));

  return (
    <>
      <div className="result-row baccarat casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          const isPlayer = win === "1" || win === 1;
          const isTie = win === "3" || win === 3;
          
          let resultClass = "result-b";
          let resultLabel = "B";
          
          if (isPlayer) {
            resultClass = "result-a";
            resultLabel = "P";
          } else if (isTie) {
            resultClass = "result-tie";
            resultLabel = "T";
          }
          
          return (
            <div
              key={`${resultItem.mid || index}-${index}`}
              className={`result-circle ${resultClass}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {resultLabel}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Baccarat 2 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          {/* Round ID Row */}
          <div className="container-fluid">
            <div className="row">
              <div className="col-md-12">
                <h6 className="text-right">
                  <b>Round Id :</b> {modalData.roundId}
                </h6>
              </div>
            </div>

            {/* Player and Banker Cards Row */}
            <div className="row bottom-border-casino m-t-10">
              <div className="col-md-12">
                <div className="player-image-container row">
                  {/* Player Column */}
                  <div className="text-center player-number w-100">
                    <div className="col br1 text-center">
                      <h4>Player</h4>
                      <div className="result-image">
                        {modalData.player?.cards?.map((card, index) => (
                          card && (
                            <img
                              key={index}
                              src={card}
                              alt={`Player Card ${index + 1}`}
                              className="mr-2"
                              onError={(e) => {
                                const cardName = card.split('/').pop();
                                const fallbackPath = `/assets/img/cards/${cardName}`;
                                if (e.target.src !== fallbackPath) {
                                  e.target.src = fallbackPath;
                                } else {
                                  e.target.style.display = 'none';
                                }
                              }}
                            />
                          )
                        ))}
                      </div>
                      {modalData.player?.isWinner && (
                        <div className="winner-icon mt-3">
                          <i className="fas fa-trophy mr-2"></i>
                        </div>
                      )}
                    </div>
                    {/* Banker Column */}
                    <div className="col text-center">
                      <h4>Banker</h4>
                      <div className="result-image">
                        {modalData.banker?.cards?.map((card, index) => (
                          card && (
                            <img
                              key={index}
                              src={card}
                              alt={`Banker Card ${index + 1}`}
                              className={`mr-2 ${index === modalData.banker?.cards?.length - 1 ? 'rrotate' : ''}`}
                              onError={(e) => {
                                const cardName = card.split('/').pop();
                                const fallbackPath = `/assets/img/cards/${cardName}`;
                                if (e.target.src !== fallbackPath) {
                                  e.target.src = fallbackPath;
                                } else {
                                  e.target.style.display = 'none';
                                }
                              }}
                            />
                          )
                        ))}
                      </div>
                      {modalData.banker?.isWinner && (
                        <div className="winner-icon mt-3">
                          <i className="fas fa-trophy mr-2"></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Result Details Row */}
            <div className="row">
              <div className="col-md-12 text-left">
                {modalData.rdescData?.winner && (
                  <p className="m-b-0 m-t-20">
                    <b>Winner:</b> {modalData.rdescData.winner}
                  </p>
                )}
                <p className="m-b-0">
                  <b>Winner Pair:</b> {modalData.rdescData?.winnerPair || '-'}
                </p>
                {modalData.rdescData?.score && (
                  <p className="m-b-0">
                    <b>Score:</b> {modalData.rdescData.score}
                  </p>
                )}
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
      
      <style>{`
        .bottom-border-casino {
          border-bottom: 1px solid #9c9c9c;
        }
        
        .m-t-10 {
          margin-top: 10px;
        }
        
        .m-t-20 {
          margin-top: 20px;
        }
        
        .player-image-container {
          margin: 0;
        }
        
        .player-number {
          padding: 10px 0;
          display: flex;
        }
        
        .result-image {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }
        
        .result-image img {
          width: auto;
          height: 80px;
          object-fit: contain;
        }
        
        .winner-icon {
          margin-top: 10px;
        }
        
        .winner-icon i {
          color: #ffd700;
          font-size: 20px;
        }
        
        .text-left {
          text-align: left;
        }
        
        .text-center {
          text-align: center;
        }
        
        .m-b-0 {
          margin-bottom: 0;
        }
        
        .mr-2 {
          margin-right: 0.5rem;
        }
        
        .mt-3 {
          margin-top: 1rem;
        }
        
        .rrotate {
          transform: rotate(15deg);
        }
      `}</style>
    </>
  );
};

export default ResultBaccarat2;

